import { useScienceTutorSection } from './scienceTutorContext';
import { ArrowUp, CheckCircle2, Droplets, RotateCcw, Sparkles, XCircle } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import {
  CONTAMINANT_LABELS,
  CONTAMINANT_ORDER,
  MAX_PLAN_STEPS,
  WATER_SCENARIOS,
  WATER_STEPS,
  getWaterScenario,
  runWaterPlan,
  validWaterPlans,
  waterStepLabel,
  type Contaminant,
  type WaterState,
  type WaterStepId,
} from './separationPlanner';
import { PlanBuilder, ResultBanner, StaleResultNotice } from './SeparationPlannerVisuals';

const WATER_MAX_STAGES = MAX_PLAN_STEPS + 1;
const OTHER_ORDERS_SHOWN = 3;

function planText(plan: WaterStepId[]): string {
  return plan.map(waterStepLabel).join(' → ');
}

/** Contaminant-by-stage grid generated from the model states. */
function ContaminantTrack({ stages }: { stages: Array<{ label: string; state: WaterState }> }) {
  const rows = CONTAMINANT_ORDER.filter((item) => stages.some((stage) => stage.state.contaminants.includes(item)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[34rem] text-left text-xs">
        <caption className="sr-only">Contaminants in the water after each stage</caption>
        <thead>
          <tr className="text-slate2">
            <th scope="col" className="py-2 pr-3 font-extrabold">Contaminant</th>
            {stages.map((stage) => (
              <th key={stage.label} scope="col" className="px-2 py-2 font-extrabold">{stage.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: Contaminant) => (
            <tr key={row} className="border-t border-hairline">
              <th scope="row" className="py-2 pr-3 font-bold text-ink">{CONTAMINANT_LABELS[row]}</th>
              {stages.map((stage) => {
                const present = stage.state.contaminants.includes(row);
                return (
                  <td key={stage.label} className="px-2 py-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-extrabold ${
                        present ? 'bg-wash-coral text-ink' : 'text-slate2'
                      }`}
                    >
                      <span aria-hidden="true">{present ? '●' : '–'}</span>
                      {present ? 'In water' : 'None'}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SeparationPlannerWater() {
  const [scenarioId, setScenarioId] = useState(WATER_SCENARIOS[0].id);
  const [plan, setPlan] = useState<WaterStepId[]>([]);
  // The train the model last ran. The plan stays editable; the result keeps describing this snapshot.
  const [ranPlan, setRanPlan] = useState<WaterStepId[] | null>(null);
  const [showWorking, setShowWorking] = useState(false);
  const planRef = useRef<HTMLElement>(null);
  useScienceTutorSection('SeparationPlannerWater', { scenarioId, plan, ranPlan, showWorking });

  const scenario = getWaterScenario(scenarioId) ?? WATER_SCENARIOS[0];
  const result = useMemo(() => (ranPlan ? runWaterPlan(scenario.id, ranPlan) : null), [ranPlan, scenario.id]);
  const validPlans = useMemo(() => (ranPlan ? validWaterPlans(scenario.id) : []), [ranPlan, scenario.id]);

  const reset = (nextId = scenario.id) => {
    setScenarioId(nextId);
    setPlan([]);
    setRanPlan(null);
    setShowWorking(false);
  };

  const run = () => setRanPlan([...plan]);
  const changePlan = () => planRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const start: WaterState = { contaminants: [...scenario.contaminants], chlorineResidual: false };
  const stages = result
    ? [
      { label: 'Source', state: start },
      ...result.outcomes
        .filter((outcome) => outcome.status === 'worked')
        .map((outcome, index) => ({ label: `${index + 1}. ${waterStepLabel(outcome.stepId)}`, state: outcome.after })),
    ]
    : [{ label: 'Source', state: start }];
  const planKey = plan.join('>');
  const ranKey = ranPlan?.join('>') ?? null;
  const stale = ranKey !== null && ranKey !== planKey;
  const otherOrders = validPlans.filter((item) => item.join('>') !== ranKey).slice(0, OTHER_ORDERS_SHOWN);

  return (
    <section className="mt-6 grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]" aria-label="Water treatment">
      <aside className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Year 7 · Applied: drinking water</p>
        <label className="mt-3 block text-sm font-extrabold text-ink" htmlFor="separation-water-source">
          Choose a water source
        </label>
        <select
          id="separation-water-source"
          value={scenario.id}
          onChange={(event) => reset(event.target.value)}
          className="mt-2 w-full rounded-2xl border border-hairline bg-surface px-3 py-2 text-sm font-extrabold text-ink"
        >
          {WATER_SCENARIOS.map((item, index) => (
            <option key={item.id} value={item.id}>
              {index + 1}. {item.title}
            </option>
          ))}
        </select>
        <h2 className="mt-5 text-xl font-extrabold text-ink">{scenario.title}</h2>
        <p className="mt-2 text-sm font-medium leading-6 text-slate2">{scenario.context}</p>
        <p className="mt-4 text-sm font-extrabold text-ink">In the source water</p>
        <ul className="mt-2 grid gap-2">
          {scenario.contaminants.map((item) => (
            <li key={item} className="flex items-center gap-2 rounded-2xl bg-surface px-3 py-2 text-sm font-bold text-ink-soft">
              <Droplets className="h-4 w-4 shrink-0 text-brand-sky" aria-hidden="true" />
              {CONTAMINANT_LABELS[item]}
            </li>
          ))}
        </ul>
      </aside>

      <div className="min-w-0 space-y-6">
        <article ref={planRef} className="scroll-mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Design the treatment train</p>
          <h2 className="mt-2 text-2xl font-extrabold text-ink">Which stage removes what, and in what order?</h2>
          <p className="mt-2 max-w-3xl font-medium leading-7 text-slate2">
            Choose only the stages this source needs and put them in order. The model checks each stage
            against what is still in the water at that point.
          </p>
          <div className="mt-5">
            <PlanBuilder
              steps={WATER_STEPS}
              plan={plan}
              maxSteps={WATER_MAX_STAGES}
              onChange={setPlan}
              noun="stage"
            />
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={plan.length === 0 || (ranKey !== null && !stale)}
              onClick={run}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Treat the water
            </button>
            <button
              type="button"
              onClick={() => reset()}
              className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Start again
            </button>
          </div>
        </article>

        {result ? (
          <article className="space-y-5 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
            {stale ? <StaleResultNotice onRun={run} runLabel="Treat with my new train" /> : null}
            <ResultBanner
              success={result.status === 'success'}
              title={result.status === 'success' ? 'Your treatment train works' : 'Not yet — the model found a problem'}
            >
              <p>{result.summary}</p>
              <p className="text-sm font-bold text-ink-soft">{scenario.teachingPoint}</p>
              {result.status === 'success' && otherOrders.length > 0 ? (
                <p className="text-sm">
                  <strong className="text-ink">Other trains that also work:</strong> {otherOrders.map(planText).join('; ')}.
                </p>
              ) : null}
              {result.status !== 'success' ? (
                <button
                  type="button"
                  onClick={changePlan}
                  className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-4 py-2 text-sm font-extrabold text-ink hover:bg-surface"
                >
                  <ArrowUp className="h-4 w-4" aria-hidden="true" /> Change my plan
                </button>
              ) : null}
            </ResultBanner>

            <ol className="grid gap-3">
              {result.outcomes.map((outcome, index) => (
                <li
                  key={outcome.stepId}
                  className={`rounded-2xl border p-4 ${outcome.status === 'worked' ? 'border-brand-mint/40 bg-white' : 'border-brand-coral/40 bg-wash-coral'}`}
                >
                  <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
                    {outcome.status === 'worked' ? (
                      <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
                    ) : (
                      <XCircle className="h-4 w-4 text-brand-coral" aria-hidden="true" />
                    )}
                    Stage {index + 1}: {waterStepLabel(outcome.stepId)} — {outcome.status === 'worked' ? 'works' : 'fails here'}
                  </p>
                  {outcome.removed.length > 0 ? (
                    <p className="mt-1 text-xs font-extrabold text-ink-soft">
                      Removes: {outcome.removed.map((item) => CONTAMINANT_LABELS[item]).join(', ')}
                    </p>
                  ) : null}
                  <p className="mt-2 text-sm font-medium leading-6 text-slate2">{outcome.message}</p>
                </li>
              ))}
            </ol>

            <ContaminantTrack stages={stages} />

            {result.status !== 'success' ? (
              <div>
                <button
                  type="button"
                  onClick={() => setShowWorking(true)}
                  disabled={showWorking}
                  className="rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink hover:bg-surface disabled:opacity-60"
                >
                  Show a working train
                </button>
                {showWorking ? (
                  <p className="mt-3 rounded-2xl bg-surface px-4 py-3 text-sm font-bold text-ink">
                    One working train: {planText(scenario.referencePlan)}.
                  </p>
                ) : null}
              </div>
            ) : null}
          </article>
        ) : null}
      </div>
    </section>
  );
}
